import type { MetricRate } from '@/types/sentinelAnalytics';

/** "45 s", "12 m", "2.5 h": the Brand Health duration format kim-ai-frontend uses. */
export function formatDuration(milliseconds: number): string {
  if (milliseconds < 60_000) {
    const seconds = milliseconds / 1000;
    return `${seconds < 10 && !Number.isInteger(seconds) ? seconds.toFixed(1) : Math.round(seconds)} s`;
  }
  if (milliseconds < 3_600_000) return `${Math.floor(milliseconds / 60_000)} m`;
  const hours = milliseconds / 3_600_000;
  return `${hours < 10 && !Number.isInteger(hours) ? hours.toFixed(1) : Math.round(hours)} h`;
}

/**
 * A wait for display, never a misleading "0 s": under a second reads "<1 s", and a business-hours
 * zero over a real clock wait (the reply came outside business hours) reads "Off-hours".
 */
export function formatWait(milliseconds: number, calendarMilliseconds?: number): string {
  if (milliseconds === 0 && calendarMilliseconds !== undefined && calendarMilliseconds >= 1000) return 'Off-hours';
  return milliseconds < 1000 ? '<1 s' : formatDuration(milliseconds);
}

/** One decimal, rounded once ("2.6%", "28%"); toFixed would print "28.0%" for 7/25 and "2.5%" for 7/274. */
export function formatPercent(rate: number | null): string {
  if (rate === null) return '—';
  return `${Math.round(rate * 1000) / 10}%`;
}

/** Card figures put a space before % ("10 %"), as in Figma; tables don't. */
export function formatCardPercent(rate: number | null): string {
  return formatPercent(rate).replace(/%$/, ' %');
}

export function formatCount(value: number): string {
  return value.toLocaleString();
}

const SINGULAR = { tickets: 'ticket', replies: 'reply' } as const;
export type SampleUnit = keyof typeof SINGULAR;

/** A count with its noun, e.g. "1 reply" or "25 tickets". */
export function formatSample(count: number, unit: SampleUnit): string {
  return `${formatCount(count)} ${count === 1 ? SINGULAR[unit] : unit}`;
}

export function formatFraction(metric: MetricRate): string {
  return `${formatCount(metric.count)}/ ${formatCount(metric.total)}`;
}
